/**
 * The first-run tour's steps (split from FirstRunTour.tsx so the component
 * file exports only the component).
 */
import { Store, Zap, MessageSquare, CalendarCheck, Plug } from 'lucide-react';
import type { MessageKey } from '../i18n';
import type { FeatureKey } from '../config/featureAvailability';

export interface TourStep {
  icon: typeof Store;
  title: MessageKey;
  body: MessageKey;
  target: string;
  /** A step a new customer cannot complete yet reads Coming soon (config/featureAvailability.ts). */
  feature?: FeatureKey;
}

/**
 * The three steps, per business kind.
 *
 * These were one hardcoded list that opened with "Link your Shopify store"
 * and went on to "activate the abandoned-cart reminder" — shown to every
 * merchant, including a workspace whose own header badge reads Service and
 * which will never have a cart. The file's header cites "kind-alignment §4";
 * the steps ignored the kind entirely.
 *
 * Only the FIRST step differs, because only the first one is about the thing
 * a merchant connects. Turning on a flow and meeting the inbox are the same
 * job whatever you sell, and inventing per-kind copy for them would be
 * variety for its own sake.
 */
const OTHER_CONNECT: TourStep = {
  icon: Plug,
  title: 'tour.step1Other.title',
  body: 'tour.step1Other.body',
  target: '/account?tab=integrations',
};

const CONNECT_STEP: Record<string, TourStep> = {
  service: {
    icon: CalendarCheck,
    title: 'tour.step1Service.title',
    body: 'tour.step1Service.body',
    target: '/business',
  },
  course: OTHER_CONNECT,
  saas: OTHER_CONNECT,
  coaching: OTHER_CONNECT,
  content_creator: OTHER_CONNECT,
  ecommerce: {
    icon: Store,
    title: 'tour.step1.title',
    body: 'tour.step1.body',
    target: '/account?tab=integrations',
    // "Connect your store": in-app Shopify connect is live (shopify.connect).
    feature: 'shopify.connect',
  },
};

/** An unknown or missing kind gets the store step, as before. */
export function stepsFor(businessType: string | undefined): TourStep[] {
  const first =
    (businessType !== undefined ? CONNECT_STEP[businessType] : undefined) ?? CONNECT_STEP.ecommerce;
  return [
    first,
    { icon: Zap, title: 'tour.step2.title', body: 'tour.step2.body', target: '/studio?tab=my-flows' },
    // WhatsApp, Instagram and Messenger: live since Meta App Review (2026-10-10);
    // the label follows the registry if that ever changes.
    { icon: MessageSquare, title: 'tour.step3.title', body: 'tour.step3.body', target: '/inbox', feature: 'messenger.dmAutomation' },
  ];
}
